import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Play, Music, Headphones, Youtube } from 'lucide-react';
import { Song } from '../data/songs';
import { Clothespin } from './Clothespin';
interface SongPhotoProps {
  song: Song;
  isSelected: boolean;
  onClick: () => void;
  index: number;
}
export function SongPhoto({
  song,
  isSelected,
  onClick,
  index
}: SongPhotoProps) {
  const [isFlipped, setIsFlipped] = useState(false);
  // Deterministic but varied rotation and duration based on index
  const baseRotation = index % 2 === 0 ? -1.8 : 1.8;
  const duration = 4 + index % 3 * 0.6;
  const delay = index * 0.25;
  // Slight horizontal offset so pins aren't perfectly centered
  const pinOffset = index % 2 === 0 ? -3 : 3;
  // Slight pin tilt variation
  const pinTilt = index % 2 === 0 ? -4 : 4;
  const handleFlip = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsFlipped(!isFlipped);
    onClick();
  };
  return <motion.div className="relative flex shrink-0 flex-col items-center pt-6" style={{
    transformOrigin: 'top center',
    perspective: '1000px' // Required for 3D flip effect
  }} animate={{
    rotateZ: [baseRotation, -baseRotation, baseRotation]
  }} transition={{
    repeat: Infinity,
    duration,
    delay,
    ease: 'easeInOut'
  }} data-id="element-170">
      <motion.div className="relative block w-44 md:w-60" style={{
      transformStyle: 'preserve-3d',
      filter: 'drop-shadow(0 12px 18px rgba(0,0,0,0.7)) drop-shadow(0 2px 4px rgba(0,0,0,0.4))'
    }} animate={{
      rotateY: isFlipped ? 180 : 0
    }} transition={{
      duration: 0.6,
      type: 'spring',
      stiffness: 260,
      damping: 20
    }} data-id="element-171">
        {/* FRONT OF PHOTO */}
        <button onClick={handleFlip} className="group relative aspect-square w-full overflow-hidden bg-zinc-900" style={{
        backfaceVisibility: 'hidden'
      }} aria-label={`Select song ${song.title}`} data-id="element-172">
          {/* Top edge shadow — wet paper under the clothespin grip */}
          <div className="absolute inset-x-0 top-0 z-10 h-8 bg-gradient-to-b from-black/50 to-transparent" data-id="element-173" />

          {/* Pinch indent where the clothespin grips */}
          <div className="absolute top-0 z-[11] h-3 w-7 -translate-x-1/2 md:w-10" style={{
          left: `calc(50% + ${pinOffset}px)`,
          background: 'radial-gradient(ellipse at 50% 0%, rgba(0,0,0,0.7) 0%, transparent 70%)'
        }} data-id="element-174" />

          {/* Vertical paper sheen */}
          <div className="absolute inset-0 z-10 bg-gradient-to-b from-white/[0.04] via-transparent to-black/20" data-id="element-175" />

          <img src={song.image} alt={song.title} className="h-full w-full object-cover" draggable={false} data-id="element-176" />

          {/* Hover Overlay: Title only */}
          <div className="absolute inset-0 z-20 flex items-center justify-center bg-black/70 px-4 text-center opacity-0 backdrop-blur-sm transition-opacity duration-300 group-hover:opacity-100" data-id="element-177">
            <span className="font-mono text-base tracking-widest text-white md:text-lg" data-id="element-178">
              {song.title}
            </span>
          </div>
        </button>

        {/* BACK OF PHOTO (Streaming Links) */}
        <div className="absolute inset-0 flex flex-col items-center justify-center bg-paper p-6 text-zinc-900" style={{
        backfaceVisibility: 'hidden',
        transform: 'rotateY(180deg)',
        backgroundImage: 'radial-gradient(circle at center, transparent 0%, rgba(0,0,0,0.05) 100%)'
      }} data-id="element-179">
          {/* Top edge shadow on the back */}
          <div className="absolute inset-x-0 top-0 h-8 bg-gradient-to-b from-black/20 to-transparent" data-id="element-180" />

          <div className="flex w-full flex-col gap-3" data-id="element-181">
            {song.spotifyUrl && <a href={song.spotifyUrl} target="_blank" rel="noopener noreferrer" className="flex w-full items-center justify-center gap-2 border border-zinc-900/20 bg-white py-2 font-mono text-xs font-bold transition-colors hover:bg-zinc-900 hover:text-white" data-id="element-182">
                <Music className="h-4 w-4" data-id="element-183" />
                SPOTIFY
              </a>}
            {song.appleMusicUrl && <a href={song.appleMusicUrl} target="_blank" rel="noopener noreferrer" className="flex w-full items-center justify-center gap-2 border border-zinc-900/20 bg-white py-2 font-mono text-xs font-bold transition-colors hover:bg-zinc-900 hover:text-white" data-id="element-184">
                <Headphones className="h-4 w-4" data-id="element-185" />
                APPLE MUSIC
              </a>}
            {song.youtubeUrl && <a href={song.youtubeUrl} target="_blank" rel="noopener noreferrer" className="flex w-full items-center justify-center gap-2 border border-zinc-900/20 bg-white py-2 font-mono text-xs font-bold transition-colors hover:bg-zinc-900 hover:text-white" data-id="element-186">
                <Youtube className="h-4 w-4" data-id="element-187" />
                YOUTUBE
              </a>}

            {/* Fallback if no links are available yet */}
            {!song.spotifyUrl && !song.appleMusicUrl && !song.youtubeUrl && <div className="text-center font-mono text-xs text-zinc-500" data-id="element-188">
                LINKS DEVELOPING...
              </div>}
          </div>

          <button onClick={handleFlip} className="absolute bottom-4 font-mono text-[10px] text-zinc-500 underline hover:text-zinc-900" data-id="element-189">
            FLIP BACK
          </button>
        </div>

        {/* Clothespin — positioned to GRIP the top of the photo. */}
        <div className="pointer-events-none absolute z-30" style={{
        top: '-28px',
        left: `calc(50% + ${pinOffset}px)`,
        transform: 'translateX(-50%)'
      }} data-id="element-190">
          <Clothespin rotate={pinTilt} data-id="element-191" />
        </div>
      </motion.div>
    </motion.div>;
}